import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { TABULAR, type TonalTokens } from "./tokens";

export type Tone = "gain" | "loss" | "flat";

/** Which colour a signed figure takes; null figures take none. Rounds to zero at 4 dp. */
export const toneOf = (value: number | null | undefined): Tone | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (Math.abs(value) < 0.00005) return "flat";
  return value > 0 ? "gain" : "loss";
};

/** A large figure's colour: gain and loss by sign, flat or unknown in `text`. */
export const toneColour = (tone: Tone | null, tokens: TonalTokens): string => {
  if (tone === "gain") return tokens.gain;
  if (tone === "loss") return tokens.loss;
  return tokens.text;
};

/** The same at 14px or under, where red and green take `lossText` and `gainText` to stay above AA. */
export const toneTextColour = (
  tone: Tone | null,
  tokens: TonalTokens,
): string => {
  if (tone === "loss") return tokens.lossText;
  if (tone === "gain") return tokens.gainText;
  return toneColour(tone, tokens);
};

export interface LabelValue {
  label: string;
  /** Text, or a node such as the source link. */
  value: ReactNode;
  tone: Tone | null;
  /** A second, quieter figure beneath. */
  sub?: string | null;
}

interface LabelValueRowProps {
  line: LabelValue;
  tokens: TonalTokens;
}

/** One `dt`/`dd` pair of a detail list: label on the left, figure on the right, a hairline above. */
export const LabelValueRow = ({ line, tokens }: LabelValueRowProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 2,
      py: 1,
      borderTop: `1px solid ${tokens.hairline}`,
      breakInside: "avoid",
    }}
  >
    <Typography
      component="dt"
      sx={{ flex: "none", fontSize: "0.8125rem", color: tokens.muted }}
    >
      {line.label}
    </Typography>
    <Box component="dd" sx={{ m: 0, minWidth: 0, textAlign: "right" }}>
      <Typography
        component="div"
        sx={{
          ...TABULAR,
          fontSize: "0.8125rem",
          fontWeight: 600,
          overflowWrap: "anywhere",
          color: toneTextColour(line.tone, tokens),
        }}
      >
        {line.value}
      </Typography>
      {line.sub !== undefined && line.sub !== null && (
        <Typography
          sx={{ ...TABULAR, fontSize: "0.6875rem", color: tokens.muted }}
        >
          {line.sub}
        </Typography>
      )}
    </Box>
  </Box>
);
